import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { Product } from "../../types/Product";
import { formatMoney } from "../../styles/liquidGlass";
import { esSuscripcion } from "@ecommerce/shared-utils";
import { Icon, ImgPh } from "../liquid/LiquidPrimitives";
import QuickAddDialog from "./QuickAddDialog";
import { useTiendaInfo } from "../../contexts/TiendaInfoContext";
import { buildProductWhatsAppUrl } from "../../utils/whatsapp";
import { trackMetric } from "@ecommerce/analytics";

interface ProductCardProps {
    product: Product;
    variant?: "carousel" | "showcase-small" | "showcase-large" | "carousel-border";
    showDiscount?: boolean;
    discountText?: string;
    onClick?: (productSku: string) => void;
    layout?: "card" | "row";
    tipoCard?: "glass" | "minimal" | "editorial" | "compacta";
}

export default function ProductCard({
    product,
    variant,
    showDiscount = false,
    discountText = "Nuevo",
    onClick,
    layout = "card",
    tipoCard = "glass",
}: ProductCardProps) {
    const [quickAddOpen, setQuickAddOpen] = useState(false);
    const tiendaInfo = useTiendaInfo();
    const isMobile = useMediaQuery("(max-width:768px)");

    const href = `/productos/${product.sku}`;
    const isOutOfStock =
        !product.permiteOrdenSinStock &&
        product.stock != null &&
        product.stock <= (product.stockMinimoPublicacion ?? 0);
    const showPrice = product.mostrarPrecio !== false;
    const isConsultOnly = product.precio === 0;
    const isSuscripcion = esSuscripcion(product.tinCodProductoFormaComercializacion);
    const isLarge = variant === "showcase-large" || tipoCard === "editorial";
    const esMinimal = tipoCard === "minimal";
    const esEditorial = tipoCard === "editorial";
    const esGlass = tipoCard === "glass";
    const mobileGlass = esGlass && isMobile;
    const showBelowImageBlock = esMinimal || esEditorial || mobileGlass;

    const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        trackMetric("PRODUCT_CLICK", { entityId: product.id.toString() });
        if (!onClick) return;
        const isLeftClick = event.button === 0;
        const isModified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
        if (isLeftClick && !isModified) {
            event.preventDefault();
            onClick(product.sku);
        }
    };

    const handleBagClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setQuickAddOpen(true);
    };

    const handleWhatsAppClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const url = buildProductWhatsAppUrl(product, tiendaInfo?.whatsappNumero);
        trackMetric("PRODUCT_WHATSAPP", { entityId: product.id.toString() });
        window.open(url, "_blank", "noopener,noreferrer");
    };

    if (layout === "row") {
        return (
            <>
                <Link
                    to={href}
                    onClick={handleLinkClick}
                    style={{
                        display: "grid",
                        gridTemplateColumns: "124px 1fr auto",
                        gap: 18,
                        alignItems: "center",
                        padding: 12,
                        borderRadius: "var(--radius-md)",
                        textDecoration: "none",
                        color: "inherit",
                        width: "100%",
                        minHeight: 148,
                        background: "var(--glass-bg)",
                        border: "var(--glass-border)",
                        backdropFilter: "var(--blur-normal)",
                        transition: "box-shadow 0.2s ease, transform 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = "var(--shadow-md)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.transform = "translateY(0)";
                    }}
                >
                    <div
                        style={{
                            borderRadius: "var(--radius-sm)",
                            overflow: "hidden",
                            height: 124,
                            background: "var(--color-surface)",
                        }}
                    >
                        <ImgPh
                            label={product.nombre}
                            src={product.urlImagenPrincipal}
                            ratio="auto"
                            style={{ height: "100%", borderRadius: "var(--radius-sm)" }}
                        />
                    </div>

                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                fontSize: 10.5,
                                fontWeight: 600,
                                letterSpacing: "0.12em",
                                textTransform: "uppercase",
                                color: isOutOfStock ? "var(--ink-400)" : "var(--color-primary)",
                                marginBottom: 6,
                            }}
                        >
                            {isOutOfStock ? "Sin stock" : "Disponible"}
                        </div>
                        <div
                            style={{
                                fontSize: 17,
                                fontWeight: 600,
                                letterSpacing: "-0.02em",
                                color: "var(--ink-900)",
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {product.nombre}
                        </div>
                        <div style={{ marginTop: 6, color: "var(--ink-400)", fontSize: 12 }}>
                            SKU {product.sku}
                        </div>
                        {showPrice && (
                            <div
                                style={{
                                    marginTop: 10,
                                    fontSize: isConsultOnly ? 13 : 19,
                                    fontWeight: 700,
                                    color: isConsultOnly ? "var(--ink-500)" : "var(--ink-900)",
                                    fontFamily: isConsultOnly ? undefined : "'SF Mono', monospace",
                                }}
                            >
                                {isConsultOnly ? (
                                    "Consultar precio"
                                ) : (
                                    <>
                                        {formatMoney(product.precio)}
                                        {isSuscripcion && (
                                            <span
                                                style={{
                                                    fontWeight: 400,
                                                    fontSize: 13,
                                                    color: "var(--ink-500)",
                                                }}
                                            >
                                                {" "}
                                                / mes
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={isConsultOnly ? handleWhatsAppClick : handleBagClick}
                        aria-label={
                            isConsultOnly ? "Consultar por WhatsApp" : "Elegir variante y cantidad"
                        }
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: "var(--radius-sm)",
                            background: isConsultOnly ? "#25D366" : "var(--color-primary)",
                            color: "var(--btn-dark-text)",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            boxShadow: "none",
                            flexShrink: 0,
                        }}
                    >
                        {isConsultOnly ? (
                            <Icon.whatsapp color="#ffffff" />
                        ) : (
                            <Icon.bag color="var(--btn-dark-text)" />
                        )}
                    </button>
                </Link>

                {!isConsultOnly && (
                    <QuickAddDialog
                        open={quickAddOpen}
                        product={product}
                        onClose={() => setQuickAddOpen(false)}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <Link
                to={href}
                onClick={handleLinkClick}
                style={{
                    position: "relative",
                    borderRadius: "var(--radius-md)",
                    overflow: "hidden",
                    cursor: "pointer",
                    background: esMinimal ? "transparent" : "var(--glass-bg)",
                    border: esMinimal ? "1px solid var(--color-border)" : "var(--glass-border)",
                    backdropFilter: esGlass ? "var(--blur-normal)" : "none",
                    boxShadow: esMinimal ? "none" : "var(--shadow-sm)",
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    display: "block",
                    width: "100%",
                }}
                onMouseEnter={(event) => {
                    event.currentTarget.style.transform = esMinimal
                        ? "translateY(-3px)"
                        : "translateY(-6px)";
                    event.currentTarget.style.boxShadow = esMinimal
                        ? "var(--shadow-sm)"
                        : "var(--shadow-md)";
                }}
                onMouseLeave={(event) => {
                    event.currentTarget.style.transform = "translateY(0)";
                    event.currentTarget.style.boxShadow = esMinimal ? "none" : "var(--shadow-sm)";
                }}
            >
                <div
                    style={{
                        position: "relative",
                        aspectRatio: esEditorial ? "4/5" : isLarge ? "1/1.1" : "1/1.05",
                        background: "var(--color-surface)",
                        borderRadius: esMinimal ? 0 : "var(--radius-md)",
                        overflow: "hidden",
                    }}
                >
                    <ImgPh
                        label={product.nombre}
                        ratio="auto"
                        src={product.urlImagenPrincipal}
                        style={{
                            height: "100%",
                            borderRadius: 0,
                            background: "var(--color-surface)",
                        }}
                    />

                    {(showDiscount || isOutOfStock) && (
                        <div
                            style={{
                                position: "absolute",
                                top: 12,
                                left: 12,
                                padding: "5px 12px",
                                borderRadius: "var(--radius-pill)",
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: "0.10em",
                                textTransform: "uppercase",
                                background: isOutOfStock
                                    ? "rgba(26,28,30,0.75)"
                                    : "var(--color-primary)",
                                color: isOutOfStock ? "#ffffff" : "var(--btn-dark-text)",
                                backdropFilter: "blur(8px)",
                            }}
                        >
                            {isOutOfStock ? "Sin stock" : discountText}
                        </div>
                    )}

                    {!showBelowImageBlock && (
                        <div
                            style={{
                                position: "absolute",
                                left: 10,
                                right: 10,
                                bottom: 10,
                                padding: "11px 14px",
                                borderRadius: "var(--radius-sm)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 10,
                                background: "var(--glass-bg-strong)",
                                backdropFilter: "var(--blur-normal)",
                                border: "var(--glass-border)",
                                boxShadow: "var(--shadow-sm)",
                            }}
                        >
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: "var(--ink-700)",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                    }}
                                >
                                    {product.nombre}
                                </div>
                                {showPrice && (
                                    <div
                                        style={{
                                            fontSize: isConsultOnly ? 11 : 14,
                                            fontWeight: 700,
                                            marginTop: 2,
                                            color: isConsultOnly
                                                ? "var(--ink-500)"
                                                : "var(--ink-900)",
                                            fontFamily: isConsultOnly
                                                ? undefined
                                                : "'SF Mono', monospace",
                                        }}
                                    >
                                        {isConsultOnly ? (
                                            "Consultar precio"
                                        ) : (
                                            <>
                                                {formatMoney(product.precio)}
                                                {isSuscripcion && (
                                                    <span
                                                        style={{
                                                            fontWeight: 400,
                                                            fontSize: 11,
                                                            color: "var(--ink-500)",
                                                        }}
                                                    >
                                                        {" "}
                                                        / mes
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={isConsultOnly ? handleWhatsAppClick : handleBagClick}
                                aria-label={
                                    isConsultOnly
                                        ? "Consultar por WhatsApp"
                                        : "Elegir variante y cantidad"
                                }
                                style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: "var(--radius-sm)",
                                    background: isConsultOnly ? "#25D366" : "var(--color-primary)",
                                    color: "var(--btn-dark-text)",
                                    border: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    boxShadow: "none",
                                    flexShrink: 0,
                                }}
                            >
                                {isConsultOnly ? (
                                    <Icon.whatsapp color="#ffffff" />
                                ) : (
                                    <Icon.bag color="var(--btn-dark-text)" />
                                )}
                            </button>
                        </div>
                    )}
                </div>
                {showBelowImageBlock && (
                    <div
                        style={{
                            padding: mobileGlass
                                ? "10px 12px 12px"
                                : esMinimal
                                  ? "14px 2px 2px"
                                  : "16px 4px 4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                        }}
                    >
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    fontSize: esEditorial ? 15 : mobileGlass ? 12.5 : 12,
                                    fontWeight: esEditorial ? 600 : 500,
                                    color: "var(--ink-700)",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    textTransform: esEditorial ? "none" : "uppercase",
                                    letterSpacing: esEditorial ? 0 : "0.04em",
                                }}
                            >
                                {product.nombre}
                            </div>
                            {showPrice && (
                                <div
                                    style={{
                                        fontSize: isConsultOnly
                                            ? 12
                                            : esEditorial
                                              ? 16
                                              : mobileGlass
                                                ? 15
                                                : 14,
                                        fontWeight: 700,
                                        marginTop: 4,
                                        color: isConsultOnly ? "var(--ink-500)" : "var(--ink-900)",
                                        fontFamily: isConsultOnly
                                            ? undefined
                                            : "'SF Mono', monospace",
                                    }}
                                >
                                    {isConsultOnly ? (
                                        "Consultar precio"
                                    ) : (
                                        <>
                                            {formatMoney(product.precio)}
                                            {isSuscripcion && (
                                                <span
                                                    style={{
                                                        fontWeight: 400,
                                                        fontSize: 12,
                                                        color: "var(--ink-500)",
                                                    }}
                                                >
                                                    {" "}
                                                    / mes
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={isConsultOnly ? handleWhatsAppClick : handleBagClick}
                            aria-label={
                                isConsultOnly
                                    ? "Consultar por WhatsApp"
                                    : "Elegir variante y cantidad"
                            }
                            style={{
                                width: mobileGlass ? 40 : esMinimal ? 30 : 34,
                                height: mobileGlass ? 40 : esMinimal ? 30 : 34,
                                borderRadius: "var(--radius-sm)",
                                background: isConsultOnly ? "#25D366" : "var(--color-primary)",
                                color: "var(--btn-dark-text)",
                                border: "none",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                boxShadow: "none",
                                flexShrink: 0,
                            }}
                        >
                            {isConsultOnly ? (
                                <Icon.whatsapp size={mobileGlass ? 18 : 16} color="#ffffff" />
                            ) : (
                                <Icon.bag
                                    size={mobileGlass ? 18 : 16}
                                    color="var(--btn-dark-text)"
                                />
                            )}
                        </button>
                    </div>
                )}
            </Link>

            {!isConsultOnly && (
                <QuickAddDialog
                    open={quickAddOpen}
                    product={product}
                    onClose={() => setQuickAddOpen(false)}
                />
            )}
        </>
    );
}
